import { useTranslation } from 'react-i18next';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import { StatusBadge, StatusTone, formatCurrency, percentChange } from '../../../components/design-system/admin';
import type { CostLevel, ProviderView } from '../../../common/models/platform-costs.model';

/** Dollars, with enough decimals that a model call's cost is not "$0.00". */
export function money(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  const abs = Math.abs(value);
  return formatCurrency(value, 'usd', abs > 0 && abs < 0.1 ? 4 : 2);
}

/** Whole dollars for big numbers on tight tiles. */
export function moneyShort(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return Math.abs(value) >= 1000 ? formatCurrency(value, 'usd', 0) : money(value);
}

const TONE: Record<CostLevel, StatusTone> = { ok: 'success', low: 'warning', critical: 'danger', unknown: 'neutral' };

/** Healthy / Low / Critical / Not connected — and "Can't read" when the provider errored. */
export function LevelBadge({ provider }: { provider: Pick<ProviderView, 'level' | 'status'> }) {
  const { t } = useTranslation();
  if (provider.status === 'error') return <StatusBadge tone="danger">{t('ADMIN_COSTS.LEVEL.error')}</StatusBadge>;
  return <StatusBadge tone={TONE[provider.level]}>{t(`ADMIN_COSTS.LEVEL.${provider.level}`)}</StatusBadge>;
}

/** Where each provider's key is made, for the "not connected" state. */
export const KEY_PAGES: Record<string, string> = {
  openrouter: 'https://openrouter.ai/settings/management-keys',
  fal: 'https://fal.ai/dashboard/keys',
  vast: 'https://cloud.vast.ai/manage-keys/',
  runpod: 'https://console.runpod.io/user/settings',
  azure: 'https://learn.microsoft.com/azure/cost-management-billing/costs/assign-access-acm-data',
};

/** Change against the period before; for spend a rise is the bad direction. */
export function SpendChange({ value, previous, className }: { value: number; previous: number; className?: string }) {
  const change = percentChange(value, previous);
  if (change === null || Math.round(change) === 0) return null;
  const up = change > 0;
  return (
    <span className={cn('inline-flex items-center text-xs tabular-nums', up ? 'text-destructive' : 'text-success', className)}>
      {up ? <ArrowUpRight className="size-3" /> : <ArrowDownRight className="size-3" />}
      {Math.abs(Math.round(change))}%
    </span>
  );
}
